import { CircularProgress, IconButton, SxProps, TextField, Theme, Tooltip } from '@mui/material';
import React from 'react';
import SendBlueIcon from '../asset/icon-send-blue-on-white.png';

// Define props interface if you're using TypeScript
interface SendButtonTextFieldProps {
    name?: string;
    label?: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSubmit: () => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    isLoading?: boolean;
    autoFocus?: boolean;
    required?: boolean;
    disabled?: boolean;
    sx?: SxProps<Theme>;
}

const MAX_CHARACTER = 70; // absolute hard cap
const SAFE_THRESHOLD = 50; // show tooltip only above this
const RECOMMENDED_SAFE = "Recommended max: 50–70 characters for best recognition";

const SendButtonTextField = React.forwardRef<HTMLInputElement, SendButtonTextFieldProps>(({
    name = null,
    label = null,
    value,
    onChange,
    onSubmit,
    onKeyDown,
    isLoading = false,
    autoFocus = false,
    required = false,
    disabled = false,
    sx,
}, inputRef) => {
    // The input is driven by internal state that always tracks the live DOM value, rather than the
    // `value` prop directly. This is essential for IME (e.g. Windows Pinyin): while composing, the
    // parent must NOT feed a stale/diverging value back onto the <input>, or React would rewrite
    // the DOM mid-composition and either duplicate the pinyin ("nihao" -> "nniihhaaoo") or wipe the
    // composition entirely (candidate popup flashes and disappears). See handleChange / the sync
    // effect below.
    const [innerValue, setInnerValue] = React.useState(value);
    const isComposingRef = React.useRef(false);

    // Adopt external value changes (e.g. cleared to "" after submit, or a prompt switch), but never
    // mid-composition — that write is exactly what breaks the IME.
    React.useEffect(() => {
        if (!isComposingRef.current) setInnerValue(value);
    }, [value]);

    const showTooltip = innerValue.length >= SAFE_THRESHOLD;
    const isOverLimit = innerValue.length > MAX_CHARACTER;

    const handleButtonClick = () => {
        if (!isOverLimit) onSubmit();
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Keep the input in lock-step with the DOM so React never writes a divergent value.
        setInnerValue(event.target.value);
        // Only notify the parent when not composing, so composition doesn't churn the parent
        // (and re-render the input) mid-composition. The final text is flushed on compositionend.
        if (!isComposingRef.current) onChange(event);
    };

    const handleCompositionStart = () => {
        isComposingRef.current = true;
    };

    const handleCompositionEnd = (event: React.CompositionEvent<HTMLInputElement>) => {
        isComposingRef.current = false;
        setInnerValue(event.currentTarget.value);
        // Commit the final composed text to the parent. Chromium fires the committed change BEFORE
        // compositionend (while still composing, so handleChange skipped the parent), so this is
        // the propagation for Chromium; on Firefox/Safari it's a harmless idempotent re-fire.
        // Synthesize a minimal ChangeEvent-shaped object instead of casting the whole
        // CompositionEvent — onChange consumers only ever read `.target.value`, and this avoids
        // exposing CompositionEvent-specific fields (e.g. `.nativeEvent`) under a ChangeEvent type
        // where callers might reasonably expect InputEvent-shaped data.
        onChange({ target: event.currentTarget } as React.ChangeEvent<HTMLInputElement>);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // Enter while the IME is composing confirms a candidate; don't let it submit the form.
        if (event.nativeEvent.isComposing || event.keyCode === 229) return;
        onKeyDown(event);
    };

    const textField = (
        <TextField
            name={name || undefined}
            required={required}
            fullWidth
            id={name || undefined}
            label={label || undefined}
            autoFocus={autoFocus}
            variant="outlined"
            inputRef={inputRef}
            disabled={disabled}
            value={innerValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            error={isOverLimit}
            helperText={isOverLimit ? `Character limit exceeded (${innerValue.length}/${MAX_CHARACTER})` : ''}
            sx={{
                ...sx,
            }}
            inputProps={{
                onCompositionStart: handleCompositionStart,
                onCompositionEnd: handleCompositionEnd,
            }}
            InputProps={{
                endAdornment: isLoading ? (
                    <CircularProgress
                        size={24}
                        sx={{ mr: 1, maxHeight: 36, maxWidth: 36 }}
                    />
                ) : (
                    <IconButton
                        type="submit"
                        disabled={disabled || isOverLimit || !innerValue}
                        onClick={handleButtonClick}
                        sx={{
                            filter: (disabled || isOverLimit || !innerValue) ? 'grayscale(100%) opacity(0.5)' : 'none',
                            cursor: (disabled || isOverLimit || !innerValue) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        <img
                            src={SendBlueIcon}
                            style={{
                                maxWidth: '24px',
                                maxHeight: '24px',
                                height: '100%',
                                width: 'auto',
                            }}
                        />
                    </IconButton>
                ),
            }}
        />
    );

    return showTooltip ? (
        <Tooltip title={RECOMMENDED_SAFE} placement="top-start" arrow>
            {textField}
        </Tooltip>
    ) : (
        textField
    );
});

export default SendButtonTextField;
